import * as React from 'react'
import PropTypes from 'prop-types'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  Collapse,
  IconButton,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { Link } from 'react-router-dom'
import { useTranslate } from 'ra-core'

const useStyles = makeStyles(theme => ({
  table: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  expandCell: { width: 48 },
  questionLink: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  responseRow: {
    backgroundColor: '#f8fafc',
  },
  responseChip: { margin: 4 },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
}))

const getLabel = (item, locale) => {
  if (!item || !item.label) return '—'
  return (
    item.label[locale] || item.label.es || Object.values(item.label)[0] || '—'
  )
}

const QuestionRow = ({ question, index, locale, translate }) => {
  const classes = useStyles()
  const [open, setOpen] = React.useState(false)
  const responses = question.responseList || []

  return (
    <>
      <TableRow hover>
        <TableCell className={classes.expandCell}>
          {responses.length > 0 && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          <Link
            to={`/questions/${question.id}/show`}
            className={classes.questionLink}
          >
            #{question.id}
          </Link>
        </TableCell>
        <TableCell>{getLabel(question, locale)}</TableCell>
        <TableCell>
          <Chip size="small" label={question.responseType} />
        </TableCell>
        <TableCell align="center">{responses.length}</TableCell>
      </TableRow>
      {responses.length > 0 && (
        <TableRow className={classes.responseRow}>
          <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box margin={2}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  color="textSecondary"
                >
                  {translate('surveys.resources.responses')}
                </Typography>
                <Box>
                  {responses.map(response => (
                    <Chip
                      key={response.id}
                      className={classes.responseChip}
                      size="small"
                      variant="outlined"
                      label={getLabel(response, locale)}
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

QuestionRow.propTypes = {
  question: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
}

const CampaignQuestionTable = ({ questionIds, questionList, locale }) => {
  const classes = useStyles()
  const translate = useTranslate()

  const orderedQuestions = React.useMemo(() => {
    if (!questionIds || questionIds.length === 0) return []
    const byId = {}
    ;(questionList || []).forEach(q => {
      byId[q.id] = q
    })
    return questionIds.map(id => byId[id]).filter(Boolean)
  }, [questionIds, questionList])

  if (orderedQuestions.length === 0) {
    return (
      <Box className={classes.empty}>
        <Typography variant="h6" gutterBottom>
          {translate('surveys.campaigns.no_questions_title')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {translate('surveys.campaigns.no_questions_hint')}
        </Typography>
      </Box>
    )
  }

  return (
    <TableContainer component={Paper} className={classes.table} elevation={0}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={classes.expandCell} />
            <TableCell>#</TableCell>
            <TableCell>ID</TableCell>
            <TableCell>{translate('surveys.common.name')}</TableCell>
            <TableCell>
              {translate('surveys.campaigns.response_type')}
            </TableCell>
            <TableCell align="center">
              {translate('surveys.resources.responses')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderedQuestions.map((question, index) => (
            <QuestionRow
              key={question.id}
              question={question}
              index={index}
              locale={locale}
              translate={translate}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

CampaignQuestionTable.propTypes = {
  questionIds: PropTypes.array,
  questionList: PropTypes.array,
  locale: PropTypes.string.isRequired,
}

export default CampaignQuestionTable
